class CartStarterPackageUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";

    this.requiredOptionCount = Number(
      this.dataset.requiredOptions || this.dataset.missingMagnets || 0,
    );
    this.crayonVariantId = Number(this.dataset.crayonVariantId || 0);
    this.upgradedCartBase = Number(this.dataset.upgradedCartBase || 0);
    this.baseBundleSavings = Number(this.dataset.baseBundleSavings || 0);
    this.orderAttribute = this.dataset.orderAttribute;
    this.orderValue = this.dataset.orderValue;
    this.lineProperty = this.dataset.lineProperty;
    this.linePropertyValue = this.dataset.linePropertyValue;
    this.options = Array.from(
      this.querySelectorAll(
        "[data-cart-starter-option], [data-cart-starter-magnet]",
      ),
    );
    this.addButton = this.querySelector("[data-cart-starter-add]");
    this.buttonLabel = this.querySelector("[data-cart-starter-button-label]");
    this.upgradedTotal = this.querySelector(
      "[data-cart-starter-upgraded-total]",
    );
    this.savings = this.querySelector("[data-cart-starter-savings]");
    this.details = this.querySelector(".mm-cart-starter__details");
    this.errorElement = this.querySelector("[data-cart-starter-error]");

    this.syncTabIndexes();
    this.options.forEach((option) => {
      option.addEventListener("change", (event) =>
        this.handleOptionChange(event),
      );
    });
    this.addButton?.addEventListener("click", () => this.addMissingProducts());
    this.details?.addEventListener("toggle", () =>
      this.collapsePopularAddons(),
    );

    this.updateButton();
  }

  collapsePopularAddons() {
    if (!this.details?.open) return;

    this.closest("cart-drawer")
      ?.querySelector(".wt-cart__cross-sell")
      ?.classList.add("wt-cart__cross-sell--collapsed");
  }

  syncTabIndexes() {
    const drawer = this.closest("cart-drawer");
    const tabIndex = drawer?.hasAttribute("open") ? "0" : "-1";

    this.querySelectorAll("summary, button, input, a").forEach((element) => {
      element.setAttribute("tabindex", tabIndex);
    });
  }

  handleOptionChange(event) {
    event.stopPropagation();

    this.hideError();
    this.updateButton();
  }

  getCheckedOptions() {
    return this.options.filter((option) => option.checked && !option.disabled);
  }

  updateButton() {
    if (!this.addButton) return;

    const selectionIsComplete =
      this.getCheckedOptions().length >= this.requiredOptionCount;
    this.addButton.disabled = !selectionIsComplete;
    this.updateCartSummary();
  }

  updateCartSummary() {
    const upgradedTotal = this.getCheckedOptions().reduce(
      (sum, option) => sum + Number(option.dataset.finalPrice || 0),
      this.upgradedCartBase,
    );
    const bundleSavings = this.getCheckedOptions().reduce(
      (sum, option) => sum + Number(option.dataset.bundleSavings || 0),
      this.baseBundleSavings,
    );

    if (this.upgradedTotal) {
      this.upgradedTotal.textContent = this.formatMoney(upgradedTotal);
    }
    if (this.savings) {
      this.savings.textContent = this.formatMoney(bundleSavings);
    }
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || "DKK";
    const locale = document.documentElement.lang || "da-DK";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(cents / 100);
  }

  getItems() {
    const items = [];
    const item = (id) => ({
      id: Number(id),
      quantity: 1,
      ...(this.lineProperty && {
        properties: { [this.lineProperty]: this.linePropertyValue },
      }),
    });

    if (this.crayonVariantId) {
      items.push(item(this.crayonVariantId));
    }

    this.getCheckedOptions().forEach((option) => {
      items.push(item(option.value));
    });

    return items;
  }

  setLoading(isLoading) {
    if (!this.addButton || !this.buttonLabel) return;

    this.addButton.disabled = isLoading;
    this.addButton.toggleAttribute("aria-busy", isLoading);
    if (isLoading) {
      this.buttonLabel.textContent = "Tilføjer…";
    } else {
      this.buttonLabel.textContent =
        this.dataset.buttonLabel || "Opgradér nu og spar 15%";
      this.updateCartSummary();
    }
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = "";
    this.errorElement.hidden = true;
  }

  async addMissingProducts() {
    if (this.addButton?.disabled) return;

    const items = this.getItems();
    const cart = document.querySelector("cart-drawer");
    const sections =
      cart && typeof cart.getSectionsToRender === "function"
        ? cart.getSectionsToRender().map((section) => section.id)
        : [];

    this.hideError();
    this.setLoading(true);
    let productsWereAdded = false;

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig("javascript"),
        headers: {
          ...fetchConfig("javascript").headers,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          ...(!this.orderAttribute && {
            sections,
            sections_url: window.location.pathname,
          }),
        }),
      });
      let cartData = await response.json();

      if (!response.ok || cartData.status) {
        throw new Error(
          cartData.description ||
            cartData.message ||
            "Produkterne kunne ikke lægges i kurven.",
        );
      }
      productsWereAdded = true;
      const addedItemsResponse = cartData;

      if (this.orderAttribute) {
        const updateResponse = await fetch(window.routes.cart_update_url, {
          ...fetchConfig("javascript"),
          headers: {
            ...fetchConfig("javascript").headers,
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            attributes: { [this.orderAttribute]: this.orderValue },
            sections,
            sections_url: window.location.pathname,
          }),
        });
        cartData = await updateResponse.json();

        if (!updateResponse.ok || cartData.status) {
          throw new Error(
            cartData.description ||
              cartData.message ||
              "Ordreinformationen kunne ikke gemmes.",
          );
        }
      }

      // Local customization: record the successful upsell without changing its discount attributes.
      await window.MinimagsCartTimeline?.recordAdd(
        addedItemsResponse,
        { bundleTitle: this.orderValue, requestedItems: items },
      );

      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source:
            this.dataset.updateSource || "cart-starter-package-upsell",
          cartData,
        });
      }

      if (cart && cartData.sections) {
        cart.renderContents(cartData, false);
        requestAnimationFrame(() => cart.closeButton()?.focus());
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      console.error(error);
      this.showError(
        productsWereAdded
          ? "Produkterne er lagt i kurven, men ordreinformationen kunne ikke gemmes. Genindlæs siden og kontrollér kurven."
          : error.message ||
              "Produkterne kunne ikke lægges i kurven. Prøv igen.",
      );
      this.setLoading(false);
      this.updateButton();
    }
  }
}

if (!customElements.get("cart-starter-package-upsell")) {
  customElements.define(
    "cart-starter-package-upsell",
    CartStarterPackageUpsell,
  );
}
